import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { Bars2Icon, PlusCircleIcon } from '@heroicons/react/24/solid';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { TiptapEditor } from '../⚛️atoms/TiptapEditor';
import { useDebounce } from 'react-use';
import ReactDragListView from 'react-drag-listview';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { Checkbox } from '../⚛️atoms/Checkbox';
import toast from 'react-hot-toast';
import { getDataAPI, patchDataAPI } from '../../Helpers/FetchAPI';
import { ONE_TOAST_ID } from '../../constant';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MarkdownWrapper } from './MarkdownWrapper';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import useLockedTranslate from '../../Hooks/useLockedTranslate';
import { TranslateButton } from '../atoms/translateButton';
import { cloneWorkbookV2Value } from './editorUtils';
import {
  buildWorkbookItemSavePayload,
  getItemByTemplateApiPath,
  getTemplateItemId,
  WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS
} from '../../utils/workbookV2';

export const WorkbookV2Radio = forwardRef(({ item, updateItem, editing, editor, oldAnswer, editingLanguage, workbook, creditId, lessonId }, ref) => {
  const [answer, setAnswer] = useState(item.answer ?? '');
  const [isCorrect, setIsCorrect] = useState(true);
  const oldQuestionInitializedRef = useRef(false);
  const { lang } = useLockedTranslate(workbook);
  const radioName = useId();

  const listItems = item.options?.items ?? [];
  const optionTranslations = item.options?.translations ?? {};

  const updateEditorItem = (updates) => {
    const nextItem = cloneWorkbookV2Value(item);
    nextItem.text = updates.text ?? nextItem.text;
    nextItem.page_number = updates.page_number ?? nextItem.page_number;
    nextItem.old_question_id = updates.old_question_id ?? nextItem.old_question_id;
    nextItem.translations = updates.translations ?? nextItem.translations;
    nextItem.options = {
      ...(nextItem.options ?? {}),
      ...(updates.options ?? {})
    };
    updateItem(nextItem);
  };

  useEffect(() => {
    if (oldAnswer?.length) {
      setAnswer(oldAnswer[0]);
    }
  }, [oldAnswer]);

  useDebounce(
    () => {
      if (!editing) {
        return;
      }

      if (!oldQuestionInitializedRef.current) {
        oldQuestionInitializedRef.current = true;
        return;
      }

      if (item.old_question_id) {
        toast.promise(
          getDataAPI(`/workbookItems/question/${item.old_question_id}`).then((response) => {
            const question = response.data.questions.find((currentQuestion) => currentQuestion._id === item.old_question_id);
            if (question.type === 'radio') {
              updateEditorItem({
                text: question.title,
                options: { items: question.options.map((option) => option.title) }
              });
            }
            return question;
          }),
          {
            loading: 'Getting Old Question Data',
            success: (question) => `The old Question was a ${question.type}`,
            error: 'Error getting old question data'
          },
          { id: 'old-question' }
        );
      }
    },
    250,
    [editing, item.old_question_id]
  );

  useDebounce(
    () => {
      if (editor) {
        return;
      }
      toast.promise(
        patchDataAPI(
          getItemByTemplateApiPath(creditId, getTemplateItemId(item)),
          buildWorkbookItemSavePayload({ answer, lessonId })
        ),
        {
          loading: 'Saving...',
          success: 'Saved',
          error: 'Error saving'
        },
        { id: ONE_TOAST_ID }
      );
    },
    WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS,
    [answer, creditId, editor, item, lessonId]
  );

  useImperativeHandle(ref, () => ({
    checkAnswers() {
      if (!item.options?.required) {
        return true;
      }
      const correctCheck = item.options?.correct_answer === answer;
      setIsCorrect(correctCheck);
      return correctCheck;
    }
  }));

  const updateListItem = (value, index) => {
    updateEditorItem({
      options: {
        items: listItems.map((entry, entryIndex) => (entryIndex === index ? value : entry))
      }
    });
  };

  const dragProps = {
    onDragEnd(fromIndex, toIndex) {
      const nextItems = [...listItems];
      const movedItem = nextItems.splice(fromIndex, 1)[0];
      nextItems.splice(toIndex, 0, movedItem);
      updateEditorItem({ options: { items: nextItems } });
    },
    nodeSelector: 'div',
    handleSelector: 'svg'
  };

  return (
    <div className={`flex w-full items-center gap-2 ${editing && 'border-pdteal rounded-lg border-2 px-8 py-12'}`}>
      {!editing && (
        <div className="relative flex flex-col">
          <div
            className={`bg-pdred/80 absolute bottom-[4%] -left-5 w-1.5 ${isCorrect ? 'h-[10%] opacity-0' : 'h-[94%] opacity-100'} transition-all duration-300 ease-out`}
          >
            <XMarkIcon className="bg-pdred/80 absolute top-0 -left-7 size-7 rounded-l-md pl-0.5 text-white" />
          </div>
          <>
            <MarkdownWrapper>{lang === 'en' ? item.text : getTranslation(item.translations, lang, 'text', item.text)}</MarkdownWrapper>
            <ul className="mb-4 ml-8">
              {listItems.map((radioItem, index) => (
                <li key={index} className="my-2 flex items-center gap-3">
                  <label className="relative">
                    <input
                      className="peer hidden"
                      type="radio"
                      name={radioName}
                      checked={answer === radioItem}
                      onChange={() => setAnswer(radioItem)}
                    />
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-pdteal absolute top-1/2 left-0 -translate-y-1/2 opacity-0 drop-shadow-md transition-opacity peer-checked:opacity-100"
                    />
                    <FontAwesomeIcon
                      icon={faCircle}
                      className="text-pdteal absolute top-1/2 left-0 -translate-y-1/2 opacity-100 transition-opacity peer-checked:opacity-0"
                    />
                    <MarkdownWrapper className="pl-10 font-normal">
                      {lang === 'en' ? radioItem : getTranslation(optionTranslations, lang, radioItem, radioItem)}
                    </MarkdownWrapper>
                  </label>
                </li>
              ))}
            </ul>
          </>
        </div>
      )}
      {editing && (
        <div className="grid w-full grid-cols-2 gap-8">
          <div className="relative">
            <p className="mb-2 text-xl/6 font-medium text-gray-900">Radio Title</p>
            <TiptapEditor content={item.text} onChange={(value) => updateEditorItem({ text: value })} />
            {editingLanguage && (
              <TranslateButton
                text={item.text}
                language={editingLanguage}
                onResponse={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            )}
          </div>
          {editingLanguage && (
            <div>
              <p className="mb-2 text-xl/6 font-medium text-gray-900">Radio Title ({editingLanguage})</p>
              <TiptapEditor
                content={getTranslation(item.translations, editingLanguage, 'text')}
                onChange={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            </div>
          )}
          <div className="col-start-1 w-2/5">
            <OneLineInput
              value={item.page_number}
              label="Page Number"
              onChange={(value) => updateEditorItem({ page_number: value })}
            />
          </div>
          <OneLineInput
            value={item.old_question_id}
            label="Old Question ID"
            onChange={(value) => updateEditorItem({ old_question_id: value })}
          />
          <Checkbox
            label="Required"
            checked={item.options?.required ?? false}
            onChange={(value) => updateEditorItem({ options: { required: value } })}
          />
          <div className="col-span-2 flex flex-col gap-3 pl-3">
            <span className="text-2xl">Radio Options:</span>
            <ReactDragListView {...dragProps}>
              {listItems.map((radioItem, index) => (
                <div className="my-2 flex items-center gap-2 pb-8" key={`${radioItem}-${index}`}>
                  <Bars2Icon className="size-10"></Bars2Icon>
                  <div className="relative w-full">
                    <OneLineInput value={radioItem} onChange={(value) => updateListItem(value, index)} />
                    {editingLanguage && (
                      <TranslateButton
                        text={radioItem}
                        language={editingLanguage}
                        onResponse={(value) =>
                          updateEditorItem({
                            options: {
                              translations: updateTranslation(optionTranslations, editingLanguage, radioItem, value)
                            }
                          })
                        }
                      />
                    )}
                  </div>
                  {editingLanguage && (
                    <OneLineInput
                      value={getTranslation(optionTranslations, editingLanguage, radioItem)}
                      onChange={(value) =>
                        updateEditorItem({
                          options: {
                            translations: updateTranslation(optionTranslations, editingLanguage, radioItem, value)
                          }
                        })
                      }
                      label={`Item ${index + 1} (${editingLanguage})`}
                    />
                  )}
                  <label className="relative">
                    <input
                      className="peer hidden"
                      type="radio"
                      name={radioName}
                      checked={radioItem === item.options?.correct_answer}
                      onChange={(event) =>
                        event.target.checked && updateEditorItem({ options: { correct_answer: radioItem } })
                      }
                    />
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className="text-pdteal absolute top-1/2 left-0 -translate-y-1/2 opacity-0 drop-shadow-md transition-opacity peer-checked:opacity-100"
                    />
                    <FontAwesomeIcon
                      icon={faCircle}
                      className="text-pdteal absolute top-1/2 left-0 -translate-y-1/2 opacity-100 transition-opacity peer-checked:opacity-0"
                    />
                    <span className="pl-10 font-normal">Correct</span>
                  </label>
                  <TrashIcon
                    className="text-pdred hover size-12 cursor-pointer"
                    onClick={() => updateEditorItem({ options: { items: listItems.toSpliced(index, 1) } })}
                  />
                </div>
              ))}
            </ReactDragListView>
            <PlusCircleIcon
              className="text-pdteal size-12 self-center drop-shadow-md hover:cursor-pointer"
              onClick={() => updateEditorItem({ options: { items: [...listItems, ''] } })}
            />
          </div>
        </div>
      )}
    </div>
  );
});
